import React from 'react';
import { useField } from 'react-final-form';

import TextInput from '@dashboard/components/editor/EditorSidebar/BlockSidebarForm/inputs/base/TextInput';
import Picker from '@dashboard/components/editor/EditorSidebar/BlockSidebarForm/inputs/raw/RawColorPickerInput/Picker';
import TriggerButton from '@dashboard/components/editor/EditorSidebar/BlockSidebarForm/inputs/raw/RawColorPickerInput/TriggerButton';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import { Box, IconButton, InputLabel, Menu, Stack, Tooltip } from '@mui/material';
import { useDocumentColors } from '@wp-block-builder/package';

type Props = {
  nameRoot: string;
  name: string;
  templateName: string;
  label: string;
};

export default function WpDocumentColorPickerInput({ label, nameRoot, name, templateName }: Props) {
  const { input } = useField(`${nameRoot}.${name}`);
  const { value, onChange } = input;

  const { input: templateInput } = useField(nameRoot);
  const secondarySwatch = useDocumentColors();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const renderIconButton = () => {
    const v = templateInput.value[templateName];
    if (typeof v !== 'string') {
      return (
        <IconButton
          size="small"
          onClick={() => {
            templateInput.onChange({
              ...templateInput.value,
              [templateName]: '',
            });
          }}
        >
          <DataObjectOutlinedIcon sx={{ fontSize: 15, color: 'cadet.500' }} />
        </IconButton>
      );
    }

    return (
      <IconButton
        sx={{ bgcolor: 'brand.blue', color: 'primary.contrastText', ':hover': { bgcolor: 'brand.blue' } }}
        size="small"
        onClick={() => {
          templateInput.onChange({
            ...templateInput.value,
            [templateName]: null,
          });
        }}
      >
        <DataObjectOutlinedIcon sx={{ fontSize: 15 }} />
      </IconButton>
    );
  };

  const renderTemplate = () => {
    const v = templateInput.value[templateName];
    if (typeof v !== 'string') {
      return null;
    }
    return (
      <Box sx={{ mt: 1, width: '100%', p: 2, bgcolor: 'cadet.100', borderRadius: 1 }}>
        <TextInput
          autofocus
          placeholder="{{ brandColor }}"
          label="Variable override"
          name={`${nameRoot}.${templateName}`}
        />
      </Box>
    );
  };

  return (
    <Stack alignItems="flex-start">
      <InputLabel sx={{ mb: 0.5 }}>{label}</InputLabel>
      <Stack width="100%" direction="row" alignItems="center" justifyContent="flex-start" spacing={0.5}>
        <TriggerButton setAnchorEl={setAnchorEl} value={value} disableReset onChange={onChange} />
        <Tooltip title="Override this color with a data variable">{renderIconButton()}</Tooltip>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ sx: { height: 'auto', padding: 0 } }}
      >
        <Picker value={value} onChange={onChange} secondarySwatch={secondarySwatch} />
      </Menu>

      {renderTemplate()}
    </Stack>
  );
}
